import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import BloodtypeOutlinedIcon from "@mui/icons-material/BloodtypeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { addLoginToken } from "../../reduxcomponents/slices/tokenSlice";
import { getAllUserPermission } from "../../api/userPermissionAPI";
import { updateUser, getPublicProfile } from "../../api/userAPI";
import { getAwards, getMyRewards } from "../../api/employeeOfMonthAPI";
import useSnackbar from "../../hooks/useSnackbar";
import {
  InfoTile,
  ProfileHero,
  RecognitionSection,
  formatAwardDate,
  getCurrentAward,
  birthdayLabel,
  daysUntilBirthday,
  tenureLabel,
} from "./profileUi";

const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const toInputDate = (value) => (value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "");
const toDisplayDate = (value) => (value && moment(value).isValid() ? moment(value).format("DD MMMM YYYY") : null);

const REWARD_STATUS_UI = {
  available: { color: "#15803d", bg: "#dcfce7", label: "Available" },
  pending: { color: "#b45309", bg: "#fef3c7", label: "Request pending" },
  used: { color: "#4c51bf", bg: "#eef0ff", label: "Used" },
  expired: { color: "#6b7280", bg: "#f3f4f6", label: "Expired" },
  upcoming: { color: "#0369a1", bg: "#e0f2fe", label: "Starts soon" },
};

// Reward leave status — only rendered on the logged-in user's own profile.
const RewardLeaveCard = ({ rewards, onApply }) => {
  if (!rewards.length) return null;
  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #eef0f6", mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <CardGiftcardIcon sx={{ color: "#764ba2" }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          My Reward Leave
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        {rewards.map((r) => {
          const status = r.rewardLeave?.status || "expired";
          const ui = REWARD_STATUS_UI[status];
          return (
            <Box
              key={r._id}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "#fafbff",
                border: "1px dashed #d6dafe",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography sx={{ fontWeight: 700 }}>1 extra paid leave · {moment(r.month, "YYYY-MM").format("MMMM YYYY")} award</Typography>
                <Typography variant="body2" color="text.secondary">
                  {status === "used" || status === "pending"
                    ? `Leave date: ${formatAwardDate(r.rewardLeave.leaveDate, "DD MMM YYYY")}`
                    : `Valid ${formatAwardDate(r.announcedAt)} – ${formatAwardDate(r.rewardLeaveValidUntil)}`}
                </Typography>
              </Box>
              <Chip label={ui.label} size="small" sx={{ bgcolor: ui.bg, color: ui.color, fontWeight: 700 }} />
              {status === "available" && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={onApply}
                  sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", textTransform: "none", fontWeight: 700 }}
                >
                  Apply now
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const ProfileDetails = ({ profile }) => {
  const tiles = [
    { icon: <EmailOutlinedIcon />, label: "Email", value: profile.email, accent: "#667eea" },
    { icon: <BadgeOutlinedIcon />, label: "Role", value: profile.role, accent: "#764ba2" },
    { icon: <ApartmentOutlinedIcon />, label: "Department", value: profile.department, accent: "#0ea5e9" },
    { icon: <SupervisorAccountOutlinedIcon />, label: "Reports To", value: profile.teamLeader?.fullName, accent: "#14b8a6" },
    { icon: <EventOutlinedIcon />, label: "Joined", value: toDisplayDate(profile.officeJoiningDate), accent: "#f97316" },
    { icon: <HourglassBottomOutlinedIcon />, label: "Tenure", value: tenureLabel(profile.officeJoiningDate), accent: "#8b5cf6" },
    { icon: <CakeOutlinedIcon />, label: "Birthday", value: profile.birthdayLabel, accent: "#ec4899" },
    {
      icon: <BloodtypeOutlinedIcon />,
      label: "Blood Group",
      value: profile.bloodGroup && (
        <Chip label={profile.bloodGroup} size="small" sx={{ fontWeight: 700, bgcolor: "#ffebee", color: "#c62828" }} />
      ),
      accent: "#ef4444",
    },
  ];

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #eef0f6", mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        About
      </Typography>
      <Grid container spacing={2}>
        {tiles.map((t) => (
          <Grid item xs={12} sm={6} lg={3} key={t.label}>
            <InfoTile {...t} />
          </Grid>
        ))}
      </Grid>
      {profile.teams?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <GroupsOutlinedIcon sx={{ color: "#667eea" }} />
            <Typography sx={{ fontWeight: 700 }}>Teams</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {profile.teams.map((t) => (
              <Chip
                key={t._id}
                label={t.isManager ? `${t.name} · Manager` : t.name}
                variant="outlined"
                sx={{ borderColor: "#c7cdfa", color: "#4c51bf", fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

const EditProfileForm = ({ initial, onCancel, onSave, saving }) => {
  const [form, setForm] = useState(initial);
  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #d6dafe", mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        Edit Profile
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Full Name" name="fullName" value={form.fullName} onChange={handleChange} fullWidth size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} fullWidth size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Department" name="department" value={form.department} onChange={handleChange} fullWidth size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select label="Blood Group" name="bloodGroup" value={form.bloodGroup} onChange={handleChange} fullWidth size="small">
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {BLOOD_GROUP_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Date of Birth" name="dob" type="date" value={form.dob} onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Office Joining Date"
            name="officeJoiningDate"
            type="date"
            value={form.officeJoiningDate}
            onChange={handleChange}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !form.fullName.trim() || !form.email.trim()}
          sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", fontWeight: 700 }}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </Stack>
    </Paper>
  );
};

const PermissionsCard = ({ permissions }) => (
  <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #eef0f6" }}>
    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
      My Access
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Only visible to you.
    </Typography>
    {permissions.length === 0 ? (
      <Typography color="text.secondary">No permissions found.</Typography>
    ) : (
      <Stack spacing={1.25}>
        {permissions.map((p) => (
          <Box key={p.module} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, minWidth: 180, textTransform: "capitalize" }}>{p.module.replace(/-/g, " ")}</Typography>
            {p.value.map((v) => (
              <Chip key={v} label={v} size="small" sx={{ bgcolor: "#f4f5fb", color: "#4b5563" }} />
            ))}
          </Box>
        ))}
      </Stack>
    )}
  </Paper>
);

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, token, refreshToken } = useSelector((state) => state.tokens) || {};
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const isOwnProfile = !userId || userId === user?._id;
  const targetId = isOwnProfile ? user?._id : userId;

  const [publicData, setPublicData] = useState(null);
  const [allAwards, setAllAwards] = useState([]);
  const [myRewards, setMyRewards] = useState([]);
  const [allUserPermissions, setAllUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError("");
    try {
      const [profileRes, awardsRes] = await Promise.all([getPublicProfile(targetId), getAwards()]);
      setPublicData(profileRes.data?.data || null);
      setAllAwards(awardsRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load this profile.");
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  useEffect(() => {
    load();
    setEditing(false);
  }, [load]);

  useEffect(() => {
    if (!isOwnProfile) return;
    getMyRewards()
      .then((res) => setMyRewards(res.data?.data || []))
      .catch(() => setMyRewards([]));
    getAllUserPermission()
      .then((res) => setAllUserPermissions(res?.items || []))
      .catch((err) => console.error("Error fetching user permissions:", err));
  }, [isOwnProfile]);

  // For the own profile the redux user is the freshest source for editable fields.
  const profile = useMemo(() => {
    const base = publicData || {};
    const merged = isOwnProfile && user
      ? {
          ...base,
          fullName: user.fullName,
          email: user.email,
          role: user.permission,
          department: user.department,
          bloodGroup: user.bloodGroup,
          officeJoiningDate: user.officeJoiningDate,
          dob: user.dob || user.dateOfBirth,
        }
      : base;

    // DOB is stored as UTC midnight of the picked date, so read its UTC parts.
    const birthday = merged.dob && moment.utc(merged.dob).isValid()
      ? { day: moment.utc(merged.dob).date(), month: moment.utc(merged.dob).month() + 1 }
      : merged.birthday || null;
    return { ...merged, birthday, birthdayLabel: birthdayLabel(birthday) };
  }, [publicData, isOwnProfile, user]);

  const currentAward = getCurrentAward(allAwards);
  const isCurrentWinner = Boolean(currentAward && String(currentAward.user?._id || currentAward.user) === String(targetId));
  const awards = publicData?.awards || [];

  const myPermissions = allUserPermissions.find((mod) => mod.role === user?.permission)?.permission || [];

  const handleSave = async (form) => {
    setSaving(true);
    const updatedUser = {
      ...user,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      department: form.department,
      bloodGroup: form.bloodGroup,
      dob: form.dob || null,
      officeJoiningDate: form.officeJoiningDate || null,
    };
    try {
      await updateUser(updatedUser, user._id);
      dispatch(addLoginToken({ token, refreshToken, user: updatedUser }));
      setEditing(false);
      showSnackbar("Profile updated successfully!", "success");
    } catch (err) {
      console.error("Profile update failed:", err);
      showSnackbar(err.response?.data?.message || "Profile update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !publicData) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {!isOwnProfile ? (
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button startIcon={<PeopleAltOutlinedIcon />} onClick={() => navigate("/people")} sx={{ textTransform: "none" }}>
          People Directory
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {publicData && !publicData.isActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This employee is no longer active.
            </Alert>
          )}

          <ProfileHero
            name={profile.fullName}
            email={profile.email}
            role={profile.role}
            department={profile.department}
            isTeamLeader={profile.isTeamLeader}
            awards={awards}
            isCurrentWinner={isCurrentWinner}
            isBirthdayToday={daysUntilBirthday(profile.birthday) === 0}
            actions={
              isOwnProfile &&
              !editing && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Edit profile
                </Button>
              )
            }
          />

          {isOwnProfile && editing && (
            <EditProfileForm
              initial={{
                fullName: profile.fullName || "",
                email: profile.email || "",
                department: profile.department || "",
                bloodGroup: profile.bloodGroup || "",
                dob: toInputDate(profile.dob),
                officeJoiningDate: toInputDate(profile.officeJoiningDate),
              }}
              saving={saving}
              onCancel={() => setEditing(false)}
              onSave={handleSave}
            />
          )}

          <RecognitionSection awards={awards} latestMonth={currentAward?.month} />

          {isOwnProfile && <RewardLeaveCard rewards={myRewards} onApply={() => navigate("/leaves?apply=reward")} />}

          <ProfileDetails profile={profile} />

          {isOwnProfile && user?.permission !== "Admin" && <PermissionsCard permissions={myPermissions} />}
        </>
      )}
      <SnackbarComponent />
    </Container>
  );
};

export default Profile;
